import {
  createContext,
  useState,
  useContext,
  ReactNode,
} from "react";
import { useAuthContext } from "./AuthProvider";

export type Settings = {
  model: string;
  dataStore: string;
  language: string;
};

type SettingsContextType = Settings & {
  updateSettings: (key: keyof Settings, value: string) => void;
};

const SettingsContext = createContext<SettingsContextType | undefined>(
  undefined
);

const initialSettings: Settings = {
  model: "SUMMARIZED",
  dataStore: "CoachingApp_SUPPORT_DOCS",
  language: "English",
};

export const useSettingsContext = (): SettingsContextType => {
  const context = useContext(SettingsContext);
  if (!context) {
    throw new Error(
      "useSettingsContext must be used within a SettingsContextProvider"
    );
  }
  return context;
};

type SettingsContextProviderProps = {
  children: ReactNode;
};

export const SettingsContextProvider = ({
  children,
}: SettingsContextProviderProps) => {
  const [settings, setSettings] = useState<Settings>(() => {
    const storedSettings = localStorage.getItem("settings");
    return storedSettings
      ? { ...initialSettings, ...JSON.parse(storedSettings) }
      : initialSettings;
  });

  const updateSettings = (key: keyof Settings, value: string) => {
    setSettings((prevSettings) => {
      const updatedSettings = {
        ...prevSettings,
        [key]: value,
      };
      localStorage.setItem("settings", JSON.stringify(updatedSettings));
      return updatedSettings;
    });
  };


  return (
    <SettingsContext.Provider
      value={{ ...settings, updateSettings }}
    >
      {children}
    </SettingsContext.Provider>
  );
};
